import React, { useMemo, useState } from 'react'
import { message } from 'antd'
import { useMsal } from '@azure/msal-react'
import MovementListView from './MovementListView'
import MovementDetailView from './MovementDetailView'
import NewMovementView from './NewMovementView'
import NotificationSettingsModal from './NotificationSettingsModal'
import { getInventoryMovementPermissions } from './inventoryMovementConstants'
import {
  addAlertEmails,
  deleteAlertEmail,
  fetchAlertEmails,
  getApiErrorMessage
} from '../../api/inventoryMovementApiService/inventoryMovementApiUtils'

const InventoryMovement = () => {
  const { instance } = useMsal()
  const { canWrite } = getInventoryMovementPermissions()
  const [view, setView] = useState('list') // 'list' | 'detail' | 'new'
  const [selectedMovementId, setSelectedMovementId] = useState(null)
  const [alertEmails, setAlertEmails] = useState([])
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  const [isAlertEmailsLoading, setIsAlertEmailsLoading] = useState(false)
  const [isAlertEmailsSaving, setIsAlertEmailsSaving] = useState(false)
  const alertEmailIds = useMemo(() => alertEmails.map(item => item.emailId), [alertEmails])

  const goToList = () => {
    setView('list')
    setSelectedMovementId(null)
  }

  const viewMovement = (movementId) => {
    setSelectedMovementId(movementId)
    setView('detail')
  }

  const handleMovementCreated = ({ movement, emailSent }) => {
    message.success(`Movement ${movement.reference} created`)
    if (emailSent) {
      message.success('Notification email sent')
    } else {
      message.warning('Notification email could not be sent')
    }
    goToList()
  }

  const openNotificationSettings = async () => {
    setIsNotificationModalOpen(true)
    setIsAlertEmailsLoading(true)
    try {
      setAlertEmails(await fetchAlertEmails(instance))
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to load alert emails'))
    } finally {
      setIsAlertEmailsLoading(false)
    }
  }

  const saveAlertEmails = async (updatedEmails) => {
    const updatedSet = new Set(updatedEmails.map(email => email.toLowerCase()))
    const existingSet = new Set(alertEmails.map(item => item.emailId.toLowerCase()))
    const toAdd = updatedEmails.filter(email => !existingSet.has(email.toLowerCase()))
    const toDelete = alertEmails.filter(item => !updatedSet.has(item.emailId.toLowerCase()))

    setIsAlertEmailsSaving(true)
    try {
      let latest = alertEmails
      if (toAdd.length > 0) {
        latest = await addAlertEmails(instance, toAdd)
      }
      for (const item of toDelete) {
        latest = await deleteAlertEmail(instance, item._id)
      }
      setAlertEmails(latest)
      setIsNotificationModalOpen(false)
      message.success('Alert emails saved')
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to save alert emails'))
      // Some calls may have succeeded before the failure, so resync with the server
      fetchAlertEmails(instance).then(setAlertEmails).catch(() => {})
    } finally {
      setIsAlertEmailsSaving(false)
    }
  }

  if (view === 'detail' && selectedMovementId) {
    return (
      <MovementDetailView
        movementId={selectedMovementId}
        onBack={goToList}
      />
    )
  }

  if (view === 'new' && canWrite) {
    return (
      <NewMovementView
        onCancel={goToList}
        onCreate={handleMovementCreated}
      />
    )
  }

  return (
    <>
      <MovementListView
        canWrite={canWrite}
        onNewMovement={() => setView('new')}
        onViewMovement={viewMovement}
        onOpenNotificationSettings={openNotificationSettings}
      />
      <NotificationSettingsModal
        open={isNotificationModalOpen}
        ccEmails={alertEmailIds}
        loading={isAlertEmailsLoading}
        saving={isAlertEmailsSaving}
        onSave={saveAlertEmails}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </>
  )
}

export default InventoryMovement
